import React, { useEffect, useState } from "react";
import { ric } from "@/lib/ric";
import { useLang } from "@/context/LanguageContext";
import { Loader2, Package } from "lucide-react";

// Combined form: Receive yarn INTO a bin. Optionally records dye lot (needed
// once an item leaves the godown). Uses the /ric/receipts movement.
export default function RICReceive() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "en" ? en : hi);
  const [bins, setBins] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    yarn_item_id: "", to_bin_id: "", kg: "", dye_lot: "", supplier: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      setBins(await ric.bins());
      setItems(await ric.yarnItems());
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    if (!form.yarn_item_id || !form.to_bin_id || !form.kg) return setMsg(t("Fill all required fields", "सभी ज़रूरी फ़ील्ड भरें"));
    setBusy(true);
    try {
      await ric.receive({ ...form, kg: parseFloat(form.kg) });
      setMsg(t("Receipt recorded", "प्राप्ति दर्ज हुई"));
      setForm({ yarn_item_id: "", to_bin_id: "", kg: "", dye_lot: "", supplier: "" });
      setItems(await ric.yarnItems());
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="ric-receive">
      <h1 className="text-2xl font-bold mb-1 flex items-center gap-2" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
        <Package size={24} /> {t("Receive yarn into bin", "बिन में यार्न दर्ज करें")}
      </h1>
      <p className="text-xs text-[#7C8E9E] mb-4">
        {t("Records an incoming stock into a specific bin. Dye lot may stay blank on legacy items but the first outgoing movement will force it.",
           "आने वाला स्टॉक बिन में दर्ज। पुराने आइटम्स पर डाई लॉट खाली छोड़ सकते हैं — पहली निकासी पर ज़रूरी होगा।")}
      </p>

      {msg && <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="ric-receive-msg">{msg}</div>}

      <form onSubmit={submit} className="hag-surface p-4 space-y-3" data-testid="ric-receive-form">
        <Field label={t("Yarn item", "यार्न आइटम")}>
          <select data-testid="ric-receive-item" value={form.yarn_item_id} onChange={(e) => setForm({ ...form, yarn_item_id: e.target.value })}
            className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
            <option value="">— {t("select", "चुनें")} —</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.yarn_type} · {i.yarn_count} · {i.colour || "—"}</option>)}
          </select>
        </Field>
        <Field label={t("To bin", "किस बिन में")}>
          <select data-testid="ric-receive-bin" value={form.to_bin_id} onChange={(e) => setForm({ ...form, to_bin_id: e.target.value })}
            className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
            <option value="">— {t("select bin", "बिन चुनें")} —</option>
            {bins.map((b) => <option key={b.id} value={b.id}>{b.bin_number}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("Weight (kg)", "वज़न (कि.)")}>
            <input data-testid="ric-receive-kg" type="number" step="0.1" min="0" value={form.kg} onChange={(e) => setForm({ ...form, kg: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          </Field>
          <Field label={t("Dye lot (optional)", "डाई लॉट (वैकल्पिक)")}>
            <input data-testid="ric-receive-lot" value={form.dye_lot} onChange={(e) => setForm({ ...form, dye_lot: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          </Field>
        </div>
        <Field label={t("Supplier", "आपूर्तिकर्ता")}>
          <input data-testid="ric-receive-supp" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })}
            className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
        </Field>
        <button type="submit" data-testid="ric-receive-submit" disabled={busy}
          className="h-11 px-4 rounded-md text-sm font-semibold text-white flex items-center gap-2"
          style={{ background: "var(--brand-navy)" }}>
          {busy && <Loader2 size={14} className="animate-spin" />} {t("Record receipt", "प्राप्ति दर्ज करें")}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{label}</div>
      {children}
    </label>
  );
}
